import { NgClass } from '@angular/common';
import { Component, computed, inject, input, LOCALE_ID, signal } from '@angular/core';
import { OffersService } from '../../../../../services/offers/offers.service';
import { finalize, forkJoin } from 'rxjs';
import { ErrorData } from '../../../../../utils/error-data/error-data';
import { Loading } from '../../../../../utils/loading/loading';
import { OfferDetailsTab } from './offer-details-tab/offer-details-tab';
import { OfferIncludedItemsTab } from './offer-included-items-tab/offer-included-items-tab';
import { ImageCarousel } from '../../../product-details/utils/image-carousel/image-carousel';
import { SafeUrl } from '@angular/platform-browser';
import { ProductsFileMgt } from '../../../../../services/product/file-management/products.file-mgt.service';
import { HasScopeDirective } from '../../../../../services/scope/has-scope.directive';
import { SCOPES } from '../../../../../services/scope/scope-constants';

@Component({
  selector: 'app-offer-details-component',
  imports: [NgClass, ErrorData, Loading, OfferDetailsTab, OfferIncludedItemsTab, ImageCarousel, HasScopeDirective],
  templateUrl: './offer-details-component.html',
  styleUrl: './offer-details-component.scss',
})
export class OfferDetailsComponent {
  private offersService = inject(OffersService);
  private locale = inject(LOCALE_ID);
  private fileMgt = inject(ProductsFileMgt);
  offerImages = signal<{ url: SafeUrl | string; isImage: boolean }[]>([]);

  isArabic = computed(() => this.locale.startsWith('ar'));
  offerId = input.required<string>();
  offerData = signal<any>(null);
  loadingOfferData = signal<boolean>(false);
  errorOfferData = signal<string | null>(null);

  activeTab = signal<'details' | 'included-items'>('details');

  offerImageUrl = signal<{ url: SafeUrl | string; isImage: boolean } | undefined>(undefined);
  readonly scopes = SCOPES;

  setActiveTab(tab: 'details' | 'included-items') {
    this.activeTab.set(tab);
  }

  loadOfferDetails() {
    this.loadingOfferData.set(true);
    this.offersService
      .getOneOffer(this.offerId())
      .pipe(
        finalize(() => {
          this.loadingOfferData.set(false);
        }),
      )
      .subscribe({
        next: (offerDetails) => {
          this.offerData.set(offerDetails);
          this.offerImageUrl.set({ url: '', isImage: false });
          this.fetchImage(offerDetails.profileImageFids[0]).subscribe({
            next: (img) => this.offerImageUrl.set(img),
            error: () => this.offerImageUrl.set({ url: '', isImage: false }),
          });

          if (offerDetails.profileImageFids && offerDetails.profileImageFids.length > 1) {
            const imageRequests = offerDetails.profileImageFids.slice(1).map((fid: any) => this.fetchImage(fid));
            forkJoin(imageRequests).subscribe({
              next: (images) => this.offerImages.set(images as any),
              error: () => this.offerImages.set([]),
            });
          }
        },
        error: (err) => {
          this.offerImageUrl.set({ url: '', isImage: false });

          this.errorOfferData.set(
            this.isArabic()
              ? err.messageAr || 'حدث بعض الخطأ'
              : err.messageEn || 'Failed to load offer details. Please try again later.',
          );
        },
      });
  }

  private fetchImage(fid: string) {
    return this.fileMgt.getImageUrl(fid);
  }

  ngOnInit() {
    this.loadOfferDetails();
  }
}
